import React, { FC, useContext, useEffect, useState } from 'react'
import Listing, { CustomAction } from "Components/Listing/Listing";
import Header from "../Components/Layout/Header";
import Catalog from "Entity/Catalog";
import Product from "Entity/Product";
import EntityInterface from "../Vendor/Definition/EntityInterface";
import DeleteModal from "../Components/Modal/DeleteModal";
import useTranslationDataUtils from "../Vendor/Utils/TranslationDataUtils";
import Api from "../Vendor/Api";
import { useNavigate, useParams } from "react-router-dom";
import GlobalLoader from "../Vendor/Components/GlobalLoader";
import { UserContext } from "../Vendor/Context/UserContextProvider";
import ProductOffCanvas from "../Components/Offcanvas/product/ProductOffcanvas";
import FormEmptyModal from "../Components/Modal/FormEmptyModal";
import RefreshModal from "../Components/Modal/RefreshModal";
import { Button, Spinner } from 'react-bootstrap';
import LoadingButton from 'Components/Button/LoadingButton';
import { useTranslation } from 'react-i18next';
import { BannerContext } from 'Vendor/Context/BannerContextProvider';
import ChoiceType, { Choice } from 'Vendor/Components/Form/Type/ChoiceType';
import SearchableChoiceType from 'Vendor/Components/Form/Type/SearchableChoiceType';

export interface CatalogProps {
}

const CatalogPage: FC<CatalogProps> = () => {
    const { t } = useTranslation()
    const { currentUser } = useContext(UserContext)
    const params = useParams()
    const navigate = useNavigate()
    const { translate } = useTranslationDataUtils()
    const { setBanner } = useContext(BannerContext);

    const [isSearchable, setSearchable] = useState<boolean>(false)
    const [show, setShow] = useState<boolean>(false)
    const [addShow, setAddShow] = useState<boolean>(false)
    const [search, setSearch] = useState<string>('')
    const [catalog, setCatalog] = useState<Catalog | undefined>()
    const [product, setProduct] = useState<Product | undefined>(undefined)
    const [toggleReload, setToggleReload] = useState<string>('')
    const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false)
    const [hasPlatform, setHasPlatform] = useState<boolean>(currentUser?.currentOrganization?.platforms
        ? currentUser?.currentOrganization?.platforms?.length > 0
        : false)
    const [isEdited, setIsEdited] = useState<boolean>(false)
    const [showImportModal, setShowImportModal] = useState<boolean>(false);
    const [showExportModal, setShowExportModal] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [externalIdentifiers, setExternalIdentifiers] = useState<Choice[]>([])
    const [selectedExternalIdentifier, setSelectedExternalIdentifier] = useState<string>('')
    const [loadingExternalIdentifiers, setLoadingExternalIdentifiers] = useState<boolean>(true)
    const [exporting, setExporting] = useState<boolean>(false);
    const userLanguage = currentUser?.currentOrganization?.defaultLanguage as string

    const [selectedSession, setSelectedSession] = useState<string>('')

    let currentSlug: string = params.slug as string;

    const handlePublish = (product: Product) => {
        setShowRefreshModal(true)
        Api.patch(`/product/${product.id}/change-status/published`)
            .then(() => { })
            .catch(() => { })
            .then(() => {
                setToggleReload(new Date().toISOString())
                setShowRefreshModal(false)
            })
    }

    const handleUnPublish = (product: Product) => {
        setShowRefreshModal(true)
        Api.patch(`/product/${product.id}/change-status/unpublished`)
            .then(() => { })
            .catch(() => { })
            .then(() => {
                setToggleReload(new Date().toISOString())
                setShowRefreshModal(false)
            })
    }
    const [customActions, setCustomActions] = useState<CustomAction[]>(
        hasPlatform ? [
            {
                method: handlePublish,
                granted: 'publish',
                i18nLabel: 'app.publish',
                variant: 'success',
            },
            {
                method: handleUnPublish,
                granted: 'unPublish',
                i18nLabel: 'app.unPublish',
                variant: 'danger',
            },
        ] : []
    )

    // remove
    const [showDelete, setShowDelete] = useState<boolean>(false)

    useEffect(() => {
        const fetchProduct = async () => {
            if (params.hasOwnProperty('id')) {
                const id = parseInt(params.id ?? '0', 10);
                if (id > 0) {
                    const response = await Api.get(`/product/${id}`);
                    setProduct(response.data as Product);
                    setShow(true);
                }
            }
        }
        const timeoutId = setTimeout(fetchProduct, 300);

        return () => clearTimeout(timeoutId);
    }, [params.id]);

    const handleDeleteClose = (reload = false) => {
        setProduct(undefined)
        setShowDelete(false)
        if (reload) {
            setToggleReload(new Date().toISOString())
        }
    }
    const handleDeleteShow = () => setShowDelete(true)

    const deleteAction = (product: EntityInterface) => {
        setProduct(product as Product)
        handleDeleteShow()
    }

    const editAction = (product: EntityInterface) => {
        Api.get(`/product/${product.id}`)
            .then((response) => {
                setProduct(response.data as Product)
                setShow(true)
            })
    }

    const editNameAction = (product: Product) => {
        setProduct({ ...product })
        setAddShow(true)
    }

    const addProduct = () => {
        setAddShow(true)
    }

    const handleClose = (reload = false) => {
        setShow(false)
        if (params.id) {
            navigate('/c/' + params.slug)
        }
        if (reload) {
            setToggleReload(new Date().toISOString())
        }
    }

    const onExited = () => {
        setProduct(undefined)
        if (isEdited) {
            setToggleReload(new Date().toISOString())
            setIsEdited(false)
        }
    }

    const onSuccess = (product: Product) => {
        setProduct(product)
        setShow(false)
        setToggleReload(new Date().toISOString())
    }

    useEffect(() => {
        setSearchable(!!(catalog && (catalog.listingSetting.searchable.length || catalog.listingSetting.searchableByName)));
    }, [catalog])

    useEffect(() => {
        let currentSlug: string = params.slug as string;
        let tempCatalog: Catalog | undefined = catalog

        if (currentUser?.currentOrganization?.catalogs.length) {
            currentUser.currentOrganization.catalogs.forEach(catalog => {
                if (translate(catalog, 'slug') === currentSlug) {
                    tempCatalog = catalog
                }
            })
        }

        if (!tempCatalog) {
            Api.get(`/c/${currentSlug}/init`)
                .then((response) => {
                    setCatalog(response.data as Catalog)
                })
        }
        else {
            setCatalog(tempCatalog)
        }
    }, [currentUser, params])

    const changeStateOnEdit = (status: boolean) => {
        setIsEdited(status)
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files.length > 0) {
            setSelectedFile(event.target.files[0]);
            event.target.value = "";
        }
    };

    useEffect(() => {
        if (showImportModal) {
            setLoadingExternalIdentifiers(true);
            Api.get(`/c/${currentSlug}/external-identifier-properties`)
                .then((response) => {
                    const externalIdentifiersChoices = Object.values(response?.data?.externalIdentifierProperties || {}).map(
                        (externalIdentifier: any) => ({
                            i18nKey: externalIdentifier.name[userLanguage].label,
                            value: externalIdentifier.id.toString(),
                        })
                    );
                    setExternalIdentifiers(externalIdentifiersChoices);
                })
                .finally(() => {
                    setLoadingExternalIdentifiers(false);
                });
        }
    }, [showImportModal, currentSlug, userLanguage]);

    const handleImportModalClose = () => {
        setSelectedFile(null)
        setShowImportModal(false);
    };

    const handleExportModalClose = () => {
        setShowExportModal(false);
    };

    const handleImportUpload = async () => {
        if (!selectedFile) {
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("externalIdentifier", selectedExternalIdentifier);
        setUploading(true);

        Api.post(`c/${currentSlug}/export-with-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            responseType: 'blob',
        })
            .then((response) => {
                let disposition = response.headers['content-disposition'];
                let filename = disposition?.split(';')[1].split('filename')[1].split('=')[1].trim().replace(/"/g, '');
                const href = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                setBanner({ type: 'success', i18nTitle: 'app.save', i18nMessage: 'app.data_saved_message' });
                handleImportModalClose();
            })
            .catch(() => { })
            .then(() => {
                setUploading(false);
            });
    };

    const handleExport = async () => {
        if (!selectedSession) {
            return;
        }

        setExporting(true);

        Api.post(`c/${currentSlug}/export-session-products`, {
            session: selectedSession
        }, {
            responseType: 'blob',
        })
            .then((response) => {
                let disposition = response.headers['content-disposition'];
                let filename = disposition?.split(';')[1].split('filename')[1].split('=')[1].trim().replace(/"/g, '');
                const href = URL.createObjectURL(response.data);

                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();

                document.body.removeChild(link);
                URL.revokeObjectURL(href);
                setBanner({ type: 'success', i18nTitle: 'app.save', i18nMessage: 'app.data_saved_message' });
                handleExportModalClose();
            })
            .catch(() => { })
            .then(() => {
                setExporting(false);
            });
    };

    if (!catalog) return <GlobalLoader />

    return <>
        <Header title={(currentUser?.previousInternal ? '#' + catalog.id + ' - ' : '') + translate(catalog, 'name')}
            search={isSearchable ? search : undefined}
            setSearch={isSearchable ? setSearch : undefined}
            add={addProduct}
            {...(currentUser?.currentOrganization?.options.includes('import_ranking') && {
                modalTitle: t('app.add_image_xlsx_products'),
                modalButton: {
                    icon: "fa-solid fa-file",
                    onClick: () => setShowImportModal(true),
                },
                showModal: showImportModal,
                closeModal: handleImportModalClose,
                renderModalContent: () => (
                    loadingExternalIdentifiers ? (
                        <div className="d-flex justify-content-center align-items-center">
                            <Spinner animation="border" />
                        </div>
                    ) : (
                        <>
                            <p>{t('app.proceed_to_export_image_xlsx_products')}</p>
                            <ChoiceType
                                i18nLabel="app.choose_external_identifier"
                                choices={[...externalIdentifiers]}
                                required={true}
                                value={selectedExternalIdentifier}
                                setValue={(data) => setSelectedExternalIdentifier(Array.isArray(data) ? data[0] : data)}
                            />
                            <div className='d-flex'>
                                <input
                                    type="file"
                                    id="file-input"
                                    onChange={handleFileChange}
                                    className="d-none"
                                    accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                />
                                <label htmlFor="file-input" className="w-100 btn btn-secondary mb-3 p-3 fw-bold text-start">
                                    {t('app.choose_a_xlsx_file')}
                                </label>
                            </div>
                            {selectedFile && (
                                <div className='d-flex justify-content-between align-items-center px-2'>
                                    <p>{selectedFile?.name}</p>
                                    <Button className='btn btn-icon-only' variant='outline' onClick={() => setSelectedFile(null)}>
                                        <i className='fa fa-close'></i>
                                    </Button>
                                </div>
                            )}
                        </>
                    )
                ),
                renderModalFooter: () => (
                    <>
                        <Button variant="secondary" onClick={handleImportModalClose}>
                            {t('app.cancel')}
                        </Button>
                        <LoadingButton variant="primary" disabled={!selectedFile} isLoading={uploading} onClick={handleImportUpload}>
                            {t('app.export')}
                        </LoadingButton>
                    </>
                )
            })}
            {...(catalog.sessionEnabled && {
                exportModalTitle: t('app.export_session_products'),
                exportModalButton: {
                    icon: "fa-solid fa-file-spreadsheet",
                    onClick: () => setShowExportModal(true),
                },
                showExportModal,
                closeExportModal: handleExportModalClose,
                renderExportModalContent: () => (
                    <SearchableChoiceType
                        apiEndpoint={`/c/${currentSlug}/sessions`}
                        value={selectedSession}
                        setValue={setSelectedSession}
                        maxDisplayedOptions={50}
                        showRemainingCount={true}
                        i18nLabel="app.session"
                        i18nPlaceholder="app.select_session"
                        i18nSearchPlaceholder=""
                        searchDelay={300}
                    />
                ),
                renderExportModalFooter: () => (
                    <>
                        <Button variant="secondary" onClick={handleExportModalClose}>
                            {t('app.cancel')}
                        </Button>
                        <LoadingButton variant="primary" disabled={!selectedSession} isLoading={exporting} onClick={handleExport}>
                            {t('app.export')}
                        </LoadingButton>
                    </>
                )
            })}
        />
        <Listing
            setting={catalog.listingSetting}
            customActions={customActions}
            search={search}
            setSearch={setSearch}
            path={`/c/${catalog.primarySlug}`}
            entity='catalog'
            id={catalog.id}
            slug={translate(catalog, 'slug', true)}
            toggleReload={toggleReload}
            setToggleReload={setToggleReload}
            editAction={editAction}
            deleteAction={deleteAction}
            selectedItems={product ? [product] : []}
        />
        {product && <ProductOffCanvas
            product={product}
            catalog={catalog}
            show={show}
            handleClose={handleClose}
            onExited={onExited}
            editAction={editAction}
            editNameAction={editNameAction}
            changeStateOnEdit={changeStateOnEdit}
        />}
        <FormEmptyModal
            i18nTitle={product ? 'app.edit_product' : 'app.add_product'}
            show={addShow}
            path={product ? `/product/${product.id}/edit-name` : `/c/${catalog.primarySlug}/new`}
            method={product ? 'patch' : 'post'}
            handleClose={() => {
                setAddShow(false)
            }}
            onSuccess={(data) => {
                setProduct(data as Product)
                setAddShow(false)
                changeStateOnEdit(true)
                if (!show) {
                    setShow(true)
                    setToggleReload(new Date().toISOString())
                }
            }}
        />
        <DeleteModal
            handleClose={handleDeleteClose}
            show={showDelete}
            path={`/product/${product?.id}`}
            name={`#${product?.id} - ${product && translate(product, 'name')}`}
            archive={true}
        />
        <RefreshModal show={showRefreshModal} />
    </>
}

export default CatalogPage
